import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  fetchLatestNews,
  createLatestNews,
  updateLatestNews,
  deleteLatestNews,
} from "../../api/controller/admin/adminLatestNewsController";
import AdminLatestNewsDialog from "./adminDialogBoxes/AdminLatestNewsDialog";
import AdminConfirmDialog from "./adminDialogBoxes/AdminConfirmDialog";
import { formatDisplayDate } from "../helper/date";
import { useFlashMessage } from "../../components/FlashMessageProvider";

const AdminLatestNews = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const { showMessage } = useFlashMessage();

  const navigate = useNavigate();
  const userType = localStorage.getItem("userType");
  const isAdmin = userType === "admin";

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const items = await fetchLatestNews();
        if (mounted) setNews(items);
      } catch (err) {
        console.error(err);
        if (mounted) setError("Failed to load latest news.");
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, []);

  if (!isAdmin) {
    return (
      <div className="p-8 text-center">
        <h2 className="text-2xl font-bold mb-4">Access Denied</h2>
        <p>You do not have permission to view this page.</p>
        <button
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded"
          onClick={() => navigate("/")}
        >
          Go Home
        </button>
      </div>
    );
  }

  const handleDialogSave = async (values) => {
    const isEditing = Boolean(editing);
    setIsSaving(true);
    try {
      if (editing) {
        await updateLatestNews(editing, values);
      } else {
        await createLatestNews(values);
      }
      const items = await fetchLatestNews();
      setNews(items);
      showMessage(isEditing ? "Latest news updated successfully." : "Latest news added successfully.", { type: "success" });
      setDialogOpen(false);
      if (isEditing) {
        setEditing(null);
      }
    } catch (err) {
      console.error(err);
      showMessage("Failed to save latest news. Check console for details.", { type: "error" });
    } finally {
      setIsSaving(false);
    }
  };

  const handleEdit = (item) => {
    setEditing(item.id || item._id);
    setDialogOpen(true);
  };

  const handleDeleteRequest = (item) => {
    setDeleteTarget({ id: item._id || item.id, title: item.title });
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget?.id) return;
    setIsDeleting(true);
    try {
      await deleteLatestNews(deleteTarget.id);
      const items = await fetchLatestNews();
      setNews(items);
      showMessage("Latest news item deleted.", { type: "success" });
      setIsDeleteDialogOpen(false);
      setDeleteTarget(null);
    } catch (err) {
      console.error(err);
      showMessage("Failed to delete news item.", { type: "error" });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    if (isDeleting) return;
    setIsDeleteDialogOpen(false);
    setDeleteTarget(null);
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <h1 className="text-3xl font-bold mb-6">Admin — Manage Latest News</h1>

      <div className="mb-4">
        <button onClick={() => { setEditing(null); setDialogOpen(true); }} className="px-4 py-2 bg-blue-600 text-white rounded">Add News</button>
      </div>
      <AdminLatestNewsDialog
        open={dialogOpen}
        onClose={() => {
          if (isSaving) return;
          setDialogOpen(false);
          setEditing(null);
        }}
        onSave={handleDialogSave}
        initial={editing ? news.find(n => n._id === editing || n.id === editing) : null}
        isSaving={isSaving}
      />

      {loading ? (
        <p className="text-gray-600">Loading latest news...</p>
      ) : error ? (
        <p className="text-red-600">{error}</p>
      ) : (
        <div className="space-y-4">
          {news.map((item) => (
            <div key={item._id || item.id} className="bg-white p-4 rounded shadow flex items-start gap-4">
              <img src={item.image} alt={item.title} className="w-28 h-20 object-cover rounded" />
              <div className="flex-1">
                <h3 className="text-xl font-semibold">{item.title}</h3>
                <p className="text-sm text-gray-600">{formatDisplayDate(item.date)} • {item.category} • {item.readTime}</p>
                <p className="mt-2 text-gray-700">{item.excerpt}</p>
                <p className="mt-2 text-gray-700">Active: {item.active ? "Yes" : "No"}</p>
                <div className="mt-3">
                  <button onClick={() => handleEdit(item)} className="text-blue-600 mr-4">Edit</button>
                  <button onClick={() => handleDeleteRequest(item)} className="text-red-600">Delete</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <AdminConfirmDialog
        open={isDeleteDialogOpen}
        title="Delete News Item"
        message={deleteTarget?.title ? `Are you sure you want to delete "${deleteTarget.title}"? This action cannot be undone.` : "Are you sure you want to delete this news item?"}
        confirmLabel="Delete"
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        isProcessing={isDeleting}
      />
    </div>
  );
};

export default AdminLatestNews;
